import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { doc, onSnapshot, updateDoc, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Room, Issue, RoomParticipant } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useVoting } from '../hooks/useVoting';
import { useClipboard } from '../hooks/useClipboard';
import { generateRoomShareLink } from '../utils/roomUtils';
import { getRoomParticipants } from '../utils/roomManager';
import IssueManager from './IssueManager';
// import UserList from './UserList';
import { Copy, User, LogOut, Save, Eye, ChevronLeft, ChevronRight } from 'lucide-react';

interface PlanningRoomProps {
  roomId: string;
  onLeaveRoom: () => void;
}

const FIBONACCI_SEQUENCE = [0, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, '?'];
const ROLE_ORDER: Record<string, number> = { Frontend: 0, Backend: 1, 'UI/UX': 2, QA: 3 };

const PlanningRoom: React.FC<PlanningRoomProps> = ({ roomId, onLeaveRoom }) => {
  const [room, setRoom] = useState<Room | null>(null);
  const [currentIssue, setCurrentIssue] = useState<Issue | null>(null);
  const [participants, setParticipants] = useState<RoomParticipant[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const { currentUser } = useAuth();
  const { copyToClipboard } = useClipboard();
  const {
    votingSession,
    createVotingSession,
    submitVote,
    revealVotes,
    calculateFinalPoints
  } = useVoting(roomId, currentIssue?.id);

  const isAdmin = room?.adminId === currentUser?.id;

  const loadCurrentIssue = useCallback(async (issueId: string) => {
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const issueDoc = await getDoc(doc(db, 'issues', issueId));

      if (issueDoc.exists()) {
        const issue = { id: issueDoc.id, ...issueDoc.data() } as Issue;
        setCurrentIssue(issue);
      }
    } catch (error) {
      console.error('Error loading current issue:', error);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'rooms', roomId), (docSnap) => {
      if (docSnap.exists()) {
        const roomData = { id: docSnap.id, ...docSnap.data() } as Room;
        setRoom(roomData);

        if (roomData.currentIssueId && roomData.currentIssueId !== currentIssue?.id) {
          loadCurrentIssue(roomData.currentIssueId);
        } else if (!roomData.currentIssueId && currentIssue) {
          setCurrentIssue(null);
        }
      }
    });
    return unsubscribe;
  }, [roomId, currentIssue?.id, loadCurrentIssue, currentIssue]);

  useEffect(() => {
    const unsubscribe = getRoomParticipants(roomId, setParticipants);
    return unsubscribe;
  }, [roomId]);

  // Subscribe to issues for navigation and next/prev logic
  useEffect(() => {
    const q = query(collection(db, 'issues'), where('roomId', '==', roomId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Issue));
      list.sort((a, b) => (a.order || 0) - (b.order || 0));
      setIssues(list);
    });
    return unsubscribe;
  }, [roomId]);

  useEffect(() => {
    if (currentIssue?.id && !votingSession) {
      createVotingSession(currentIssue.id);
    }
  }, [currentIssue?.id, votingSession, createVotingSession]);

  const handleSelectIssue = async (issueId: string) => {
    if (!isAdmin) return;

    const issueDoc = await import('firebase/firestore').then(({ doc, getDoc }) =>
      getDoc(doc(db, 'issues', issueId))
    );

    if (issueDoc.exists()) {
      const issue = { id: issueDoc.id, ...issueDoc.data() } as Issue;
      setCurrentIssue(issue);

      if (room) {
        await updateDoc(doc(db, 'rooms', room.id), {
          currentIssueId: issueId
        });
      }
    }
  };

  const handleVote = async (points: number | string) => {
    if (!currentUser?.role || !votingSession) return;

    const voteValue = points === '?' ? 0 : Number(points);
    await submitVote(currentUser.id, currentUser.role, voteValue);
  };

  const handleShareRoom = async () => {
    const shareLink = generateRoomShareLink(roomId);
    await copyToClipboard(shareLink);
  };

  const handleSaveEstimate = async () => {
    if (!currentIssue || !votingSession?.votes || !isAdmin) return;

    try {
      const finalPoints = calculateFinalPoints(
        votingSession.votes.filter(v => participants.some(p => p.userId === v.userId))
      );

      // Save to issue
      await updateDoc(doc(db, 'issues', currentIssue.id), {
        points: finalPoints,
        isFinalized: true
      });

      // After finalizing, jump to the next issue that is not finalized yet
      try {
        const currentIndex = issues.findIndex(i => i.id === currentIssue.id);
        const next = issues.slice(currentIndex + 1).find(i => !i.isFinalized) || issues.find(i => !i.isFinalized);
        if (next && room && next.id !== currentIssue.id) {
          await updateDoc(doc(db, 'rooms', room.id), { currentIssueId: next.id });
        }
      } catch (_e) {
        // noop
      }

      // Optional: Show success feedback
      console.log('Estimate saved successfully');
    } catch (error) {
      console.error('Error saving estimate:', error);
    }
  };

  // Calculate role-based estimates
  const getRoleEstimates = () => {
    if (!participants.length) return {} as Record<string, number>;

    // Consider only votes from current participants
    const participantIds = new Set(participants.map(p => p.userId));

    // Get roles that actually exist in participants
    const existingRoles = Array.from(new Set(participants.map(p => p.role)));

    const roleVotes: { [key: string]: number[] } = {};
    if (votingSession?.votes) {
      votingSession.votes
        .filter(v => participantIds.has(v.userId))
        .forEach(vote => {
          if (!roleVotes[vote.role]) roleVotes[vote.role] = [];
          roleVotes[vote.role].push(vote.points);
        });
    }

    const estimates: { [key: string]: number } = {};
    existingRoles.forEach(role => {
      if (roleVotes[role] && roleVotes[role].length > 0) {
        estimates[role] = Math.round(roleVotes[role].reduce((a, b) => a + b, 0) / roleVotes[role].length);
      } else {
        // Show 0 for roles that exist but haven't voted yet
        estimates[role] = 0;
      }
    });

    return estimates;
  };

  const getAverageRate = () => {
    const estimates = getRoleEstimates();
    const values = Object.values(estimates);
    if (values.length === 0) return 0;
    // Sum of role averages (FE + BE + UI/UX + QA)
    return values.reduce((a, b) => a + b, 0);
  };

  const getVotingProgress = () => {
    const total = participants.length;
    if (!total || !votingSession) return { voted: 0, total };

    const participantIds = new Set(participants.map(p => p.userId));
    const voted = votingSession.votes.filter(v => participantIds.has(v.userId)).length;
    return { voted, total };
  };

  // Check if votes within a role have different values
  const hasRoleDisagreement = (role: string) => {
    if (!votingSession?.votes || !votingSession.isRevealed) return false;

    const participantIds = new Set(participants.map(p => p.userId));
    const roleVotes = votingSession.votes
      .filter(v => v.role === role && participantIds.has(v.userId))
      .map(v => v.points);

    if (roleVotes.length <= 1) return false;

    // Check if all votes are the same
    const firstVote = roleVotes[0];
    return !roleVotes.every(vote => vote === firstVote);
  };

  // Check if a participant's vote differs from the majority in their role
  const isParticipantOutlier = (participantId: string, role: string) => {
    if (!votingSession?.votes || !votingSession.isRevealed) return false;

    const participantIds = new Set(participants.map(p => p.userId));
    const roleVotes = votingSession.votes
      .filter(v => v.role === role && participantIds.has(v.userId))
      .map(v => v.points);

    if (roleVotes.length <= 1) return false;

    // Find the most common vote (mode)
    const voteCounts: Record<number, number> = {};
    roleVotes.forEach(vote => {
      voteCounts[vote] = (voteCounts[vote] || 0) + 1;
    });

    const mostCommonVote = Number(Object.keys(voteCounts).reduce((a, b) =>
      voteCounts[Number(a)] > voteCounts[Number(b)] ? a : b
    ));

    // Check if this participant's vote differs from the most common
    const participantVote = votingSession.votes.find(v => v.userId === participantId)?.points;
    return participantVote !== undefined && participantVote !== mostCommonVote;
  };

  // Participants sorted by role (FE, BE, UI/UX, QA)
  const sortedParticipants = useMemo(() => {
    return [...participants].sort((a, b) => {
      const ra = ROLE_ORDER[a.role] ?? 99;
      const rb = ROLE_ORDER[b.role] ?? 99;
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name);
    });
  }, [participants]);

  if (!room || !currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  const roleEstimates = getRoleEstimates();
  const averageRate = getAverageRate();
  const progress = getVotingProgress();
  const userVote = votingSession?.votes.find(v => v.userId === currentUser.id);

  // Navigation helpers
  const goToIssue = async (issueId: string) => {
    if (!isAdmin || !room) return;
    await updateDoc(doc(db, 'rooms', room.id), { currentIssueId: issueId });
  };

  const goNextIssue = async () => {
    if (!currentIssue || issues.length === 0) return;
    const idx = issues.findIndex(i => i.id === currentIssue.id);
    const next = issues[idx + 1] || null;
    if (next) await goToIssue(next.id);
  };

  const goPrevIssue = async () => {
    if (!currentIssue || issues.length === 0) return;
    const idx = issues.findIndex(i => i.id === currentIssue.id);
    const prev = issues[idx - 1] || null;
    if (prev) await goToIssue(prev.id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900  relative overflow-hidden">
      {/* Background Effects removed in favor of animated gradient */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>
      {/* Navbar */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-3">
        {/* Logo and Brand */}
        <div className="flex items-center gap-3">
          {/* Scrumo Logo - Two overlapping cards */}
          <div className="relative">
            {/* Back card (rotated left) */}
            <div className="w-8 h-11 bg-white/20 backdrop-blur-sm rounded border border-gray-600 flex flex-col items-center justify-center transform -rotate-12 relative">
              <div className="absolute top-1 left-1 text-[6px] font-bold text-indigo-600">5</div>
              <div className="text-lg font-bold text-white">5</div>
              <div className="absolute bottom-1 right-1 text-[6px] font-bold text-indigo-600 rotate-180">5</div>
            </div>
            {/* Front card (rotated right) */}
            <div className="absolute top-0 left-4 w-8 h-11 bg-white/20 backdrop-blur-sm rounded border border-gray-600 flex flex-col items-center justify-center transform rotate-12">
              <div className="absolute top-1 left-1 text-[6px] font-bold text-gray-700">1</div>
              <div className="text-lg font-bold text-white">1</div>
              <div className="absolute bottom-1 right-1 text-[6px] font-bold text-gray-700 rotate-180">1</div>
            </div>
          </div>
          <h1 className="text-white text-xl font-medium">Scrumo</h1>
        </div>

        {/* User Profile */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-blue-400 to-purple-500 border-2 border-white/30">
            {currentUser.avatar ? (
              <img src={currentUser.avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
            )}
          </div>
          <div className="text-white">
            <div className="font-semibold text-sm">{currentUser.name}</div>
            <div className="text-xs opacity-80">{currentUser.role}</div>
          </div>
          <button
            onClick={onLeaveRoom}
            className="w-9 h-9 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <LogOut className="w-4 h-4 text-white" />
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="px-6 pb-6">
        <div className="flex gap-6 h-[calc(100vh-100px)]">
          {/* Left Column - Issues (fills remaining space) */}
          <div className="flex-1 min-w-[280px] bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 p-6">
            <IssueManager
              roomId={roomId}
              isAdmin={isAdmin}
              onSelectIssue={handleSelectIssue}
              currentIssueId={currentIssue?.id}
            />
          </div>

          {/* Center Column - Voting area (fixed width) */}
          <div className="w-[520px] shrink-0 space-y-6">
            {/* Current Issue Display */}
            {currentIssue && (
              <div className="bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 p-6">
                <div className="flex flex-col items-center gap-3 mb-6">
                  <div className="flex items-center gap-3 text-white/80 text-sm">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{progress.voted}/{progress.total}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-semibold">Name room</span>
                    <button
                      onClick={handleShareRoom}
                      className="p-1 hover:bg-white/20 rounded transition-colors"
                    >
                      <Copy className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>

                {/* Progress Section */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-4 mb-6">
                  <h3 className="text-white font-semibold text-center mb-4">{currentIssue.title}</h3>

                  {/* Progress Bar */}
                  <div className="relative mb-6">
                    <div className="w-full h-1 bg-white/30 rounded-full">
                      <div
                        className="h-full bg-purple-500 rounded-full transition-all duration-300"
                        style={{ width: `${progress.total > 0 ? (progress.voted / progress.total) * 100 : 0}%` }}
                      />
                    </div>
                    {/* Progress Tooltip */}
                    <div
                      className="absolute -top-8 bg-purple-600 text-white text-xs px-2 py-1 rounded-full transition-all duration-300"
                      style={{ left: `${(progress.voted / progress.total) * 100}%`, transform: 'translateX(-50%)' }}
                    >
                      {progress.voted}/{progress.total}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center">
                    <div className="flex gap-3">
                      {isAdmin && votingSession?.isRevealed && votingSession?.votes && votingSession.votes.length > 0 ? (
                        <button
                          onClick={handleSaveEstimate}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500/80 hover:bg-green-500 border border-green-400/30 rounded-full text-white text-sm transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          Save & Finalize
                        </button>
                      ) : (
                        <button
                          disabled
                          className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full text-white/50 text-sm cursor-not-allowed"
                        >
                          <Save className="w-4 h-4" />
                          Save
                        </button>
                      )}
                      {isAdmin && votingSession?.votes && votingSession.votes.length > 0 && !votingSession.isRevealed && (
                        <button
                          onClick={revealVotes}
                          className="flex items-center gap-2 px-4 py-2 bg-orange-500/80 hover:bg-orange-500 border border-orange-400/30 rounded-full text-white text-sm transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          Reveal
                        </button>
                      )}
                      <button className="w-9 h-9 bg-white/10 border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={goPrevIssue}
                        disabled={!isAdmin}
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-white transition-colors border ${
                          isAdmin ? 'bg-white/10 border-white/20 hover:bg-white/20' : 'bg-white/10 border-white/20 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={goNextIssue}
                        disabled={!isAdmin}
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-white transition-colors ${
                          isAdmin ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-600 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Voting Cards */}
            <div className="bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 p-6">
              <h2 className="text-purple-300 text-xl font-semibold text-center mb-8">Choose your estimate!</h2>

              <div className="grid grid-cols-6 gap-4 justify-items-center">
                {FIBONACCI_SEQUENCE.map((value) => {
                  const isSelected = userVote?.points === (value === '?' ? 0 : Number(value));
                  return (
                    <button
                      key={value}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleVote(value);
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      className={`relative w-20 h-28 rounded-2xl border bg-white/10 backdrop-blur-sm flex flex-col items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-white/20 active:scale-95 cursor-pointer ${
                        isSelected
                          ? 'border-purple-500 border-2 bg-purple-500/20 scale-105'
                          : 'border-white/20'
                      }`}
                    >
                      <div className="absolute top-2 left-2 text-xs font-semibold text-white">{value}</div>
                      <div className="text-lg font-semibold text-white">{value}</div>
                      <div className="absolute bottom-2 right-2 text-xs font-semibold text-white rotate-180">{value}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column - Team Stats & Participants (fills remaining space) */}
          <div className="flex-1 min-w-[280px] space-y-6">
            {/* Role Estimates */}
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(roleEstimates).map(([role, value]) => {
                const hasDisagreement = hasRoleDisagreement(role);
                return (
                  <div
                    key={role}
                    className={`bg-white/15 backdrop-blur-md rounded-2xl p-4 text-center transition-all ${
                      hasDisagreement ? 'border-2 border-red-500' : 'border border-white/20'
                    }`}
                  >
                    <div className="text-white/80 text-xs mb-2 px-2 py-1 bg-white/20 rounded-full inline-block">
                      {role === 'Frontend' ? 'FE' : role === 'Backend' ? 'BE' : role}
                    </div>
                    <div className="text-white text-xl font-semibold">{votingSession?.isRevealed ? value : '?'}</div>
                  </div>
                );
              })}
            </div>

            {/* Average Rate */}
            <div className="bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 p-6">
              <div className="mb-4">
                <div className="text-purple-400 text-sm mb-1">The mean of all values.</div>
                <div className="text-white text-lg font-semibold">Total point</div>
              </div>

              <div className="flex justify-between items-end">
                <div className="text-white text-5xl font-semibold">{votingSession?.isRevealed ? averageRate : '?'}</div>
                {/* Simple chart representation */}
                <div className="flex items-end gap-1 h-24">
                  {[20, 45, 30, 60, 35, 50, 25].map((height, i) => (
                    <div
                      key={i}
                      className="w-3 bg-gradient-to-t from-purple-500 to-white rounded-t"
                      style={{ height: `${height}%` }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Participants */}
            <div className="bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 p-6 flex-1 flex flex-col">
              <h3 className="text-purple-300 text-lg font-semibold mb-6">Participant</h3>

              <div className="space-y-3 overflow-y-auto max-h-[300px] pr-2">
                {sortedParticipants.map((participant) => {
                  const hasVoted = votingSession?.votes.some(vote => vote.userId === participant.userId);
                  const userVote = votingSession?.votes.find(vote => vote.userId === participant.userId);
                  const isOutlier = isParticipantOutlier(participant.userId, participant.role);

                  return (
                    <div key={participant.id} className={`flex items-center justify-between py-3 px-4 bg-white/10 backdrop-blur-sm rounded-full transition-all ${
                      isOutlier ? 'border-2 border-red-500' : 'border border-white/10'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-blue-400 to-purple-500">
                          {participant.avatar ? (
                            <img src={participant.avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-semibold text-sm">{participant.name}</span>
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <span className="text-white/80 text-xs">
                              {participant.isAdmin ? 'host' : 'voter'}
                            </span>
                          </div>
                          <div className="text-white/60 text-xs px-2 py-1 bg-white/20 rounded-full inline-block mt-1">
                            {participant.role}
                          </div>
                        </div>
                      </div>

                      <div className="text-white font-semibold px-3 py-1 bg-white/20 rounded-full text-sm">
                        {votingSession?.isRevealed && userVote
                          ? userVote.points
                          : hasVoted ? '✓' : '-'
                        }
                      </div>
                    </div>

                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanningRoom;
